import { useState, useRef, useEffect } from "react";
import { PurchasedPackageCard } from "./PurchasedPackageCard";
import { animate, motion, useMotionValue } from "framer-motion";
import { UserAvatar } from "@/components/user/UserAvatar";
import { useAppNavigate } from "@/lib/AppNavigatorHook";
import { PurchasedPackage } from "@/services/student/packages.interfaces";

interface PurchasedPackagesSectionProps {
  packages: PurchasedPackage[];
  highlightPackageId?: string | null;
}

export const PurchasedPackagesSection = ({
  packages,
  highlightPackageId = null,
}: PurchasedPackagesSectionProps) => {
  const x = useMotionValue(0);
  const [dragConstraints, setDragConstraints] = useState({ left: 0, right: 0 });
  const [availableWidth, setAvailableWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [lastDragTime, setLastDragTime] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const appNavigation = useAppNavigate();

  const updateAvailableWidth = () => {
    if (sectionRef.current) {
      const sectionWidth = sectionRef.current.clientWidth;
      const contentWidth = sectionWidth - 48;
      setAvailableWidth(contentWidth > 0 ? contentWidth : 0);
    }
  };

  useEffect(() => {
    updateAvailableWidth();
    window.addEventListener("resize", updateAvailableWidth);

    return () => {
      window.removeEventListener("resize", updateAvailableWidth);
    };
  }, []);

  useEffect(() => {
    const updateDragConstraints = () => {
      if (containerRef.current && carouselRef.current && availableWidth > 0) {
        const carouselWidth = carouselRef.current.scrollWidth;

        if (carouselWidth > availableWidth) {
          setDragConstraints({
            left: -(carouselWidth - availableWidth),
            right: 0,
          });
        } else {
          setDragConstraints({ left: 0, right: 0 });
        }
      }
    };

    updateDragConstraints();
  }, [availableWidth, packages]);

  useEffect(() => {
    if (!highlightPackageId || !carouselRef.current) return;

    const index = packages.findIndex((pkg) => pkg.id === highlightPackageId);
    if (index < 0) return;

    const card = carouselRef.current.children[index] as HTMLElement | undefined;
    if (!card) return;

    const maxLeft = dragConstraints.left;
    const targetX = Math.max(maxLeft, Math.min(0, -card.offsetLeft));
    animate(x, targetX, { type: "spring", stiffness: 280, damping: 32 });

    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    window.requestAnimationFrame(() => {
      card.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
    });
  }, [highlightPackageId, packages, dragConstraints.left, x]);

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setLastDragTime(Date.now());
  };

  const handleRenew = (pkg: PurchasedPackage) => {
    appNavigation.goToCheckout(pkg.id, {
      name: pkg.name,
      modality: pkg.modality,
      level: pkg.level,
      duration: pkg.duration,
      modulesCount: pkg.modulesCount,
      discount: pkg.discount,
      imageUrl: pkg.imageUrl || pkg.image,
    });
  };

  const handleOnPurchaseClick = (pkg: PurchasedPackage) => {
    const timeSinceLastDrag = Date.now() - lastDragTime;
    if (isDragging || timeSinceLastDrag < 300) {
      return;
    }

    appNavigation.goToPackageModules(pkg.id, {
      name: pkg.name,
      progress: pkg.studentProgress?.progress ?? 0,
      status: pkg.studentProgress?.status ?? "not_started",
      modules: pkg.modulesCount,
      modality: pkg.modality,
      level: pkg.level,
      duration: pkg.duration,
      startDate: pkg.studentProgress?.startDate,
      endDate: pkg.studentProgress?.endDate,
    });
  };

  return (
    <div id="meus-pacotes" className="overflow-x-hidden w-full">
      <div className="bg-[#1A1A1A] absolute left-0 top-0 right-0 h-[600px]"></div>

      <div
        id="purchased-packages-section"
        className="w-full mx-auto relative z-10"
        ref={sectionRef}
      >
        <div className="px-6 pt-8 w-full">
          <div className="flex flex-col w-full mb-8">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24">
                  <UserAvatar size="xl" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl text-white font-medium">
                    Meus Pacotes
                  </h1>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-2 text-white/60 mb-6">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 5L9 12L16 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Deslize para ver mais</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>

          <div className="relative overflow-hidden w-full">
            <div
              className="pb-4 w-full overflow-hidden"
              ref={containerRef}
              style={{ width: availableWidth }}
            >
              <motion.div
                ref={carouselRef}
                drag="x"
                dragConstraints={dragConstraints}
                dragElastic={0.1}
                dragTransition={{ bounceStiffness: 600, bounceDamping: 20 }}
                className="flex gap-4 pb-4 w-max"
                style={{ x }}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                {packages.map((pkg) => (
                  <PurchasedPackageCard
                    onClick={() => handleOnPurchaseClick(pkg)}
                    onRenew={
                      pkg.isValidNow === false
                        ? () => handleRenew(pkg)
                        : undefined
                    }
                    key={pkg.id}
                    pkg={pkg}
                    highlighted={highlightPackageId === pkg.id}
                  />
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
